import { useState, useEffect } from "react";
import {
  convertToCurrencyFormat,
  isReadOnlyUser,
} from "../../../shared/utility";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { InputNumber } from "primereact/inputnumber";
import ReceivingSubTable1 from "./subtables/ReceivingSubtable1";
import TableLoadingSkeleton from "../../shared/TableLoadingSkeleton";
import Button from "../../controls/Button";

export default function ReceivingItemsTableComponent(props) {
  const {
    loading,
    values,
    askReturnSelectedItems,
    selectedItems,
    setSelectedItems,
    onUpdateUnitCostForReceivingItem,
    onUpdateTotalCostForReceivingItem,
    closed,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    fetchRowDetails,
  } = props;

  // optional pagination props from parent
  const { onFetchPage, rowsPerPage: rowsPerPageProp } = props;
  const [currentPage, setCurrentPage] = useState(1);

  const [expandedRows, setExpandedRows] = useState(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(rowsPerPageProp || 15);

  const allowExpansion = (rowData) => {
    return true;
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.unique_id]) {
      fetchRowDetails(rowData);
    }
  };

  const rowExpansionTemplate = (data) => {
    return (
      <ReceivingSubTable1
        data={data}
        loadingRows={loadingRows}
        rowDetails={rowDetails}
        fetchSubRowDetails={fetchSubRowDetails}
        subRowDetails={subRowDetails}
        subLoadingRows={subLoadingRows}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        closed={closed}
      />
    );
  };

  // Determine whether items is paginated (Laravel LengthAwarePaginator shape)
  const isPaginated =
    values &&
    values.items &&
    typeof values.items === "object" &&
    !Array.isArray(values.items) &&
    Object.prototype.hasOwnProperty.call(values.items, "data") &&
    Object.prototype.hasOwnProperty.call(values.items, "current_page");

  const items = isPaginated
    ? values.items.data
    : Array.isArray(values.items)
    ? values.items
    : [];
  const perPage = isPaginated ? values.items.per_page : rowsPerPageProp || rows;
  const totalRecords = isPaginated ? values.items.total : items.length;

  // keep rows/perPage in sync when values change
  useEffect(() => {
    if (perPage && perPage !== rows) {
      setRows(perPage);
    }
    // rows intentionally excluded to avoid loop; setRows is safe here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  // sync `first` offset from incoming paginator (so UI stays in sync when parent updates)
  const itemsPagin = values && values.items ? values.items : null;
  useEffect(() => {
    if (isPaginated && itemsPagin) {
      const newFirst =
        (itemsPagin.current_page - 1) * (itemsPagin.per_page || rows);
      setFirst(newFirst);
    } else {
      setFirst(0);
    }
    // rows intentionally excluded to avoid loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginated, itemsPagin]);

  const bodyProductName = (item) => {
    return (
      <p>
        {item.product_name} <br /> {item.plu}
      </p>
    );
  };

  const formatTwoDecimals = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue.toFixed(2) : value;
  };

  const bodyParentTotalWeight = (item) => {
    return (
      <p>
        {formatTwoDecimals(item.total_weight)}
        {item.weight_stock_unit_of_measurement_name}
      </p>
    );
  };

  const bodyParentItemWeight = (item) => {
    return (
      <p>
        {formatTwoDecimals(item.net_weight)}
        {item.weight_stock_unit_of_measurement_name}
      </p>
    );
  };

  const bodyParentPieceCount = (item) => {
    return <p>{item.total_pieces} pieces</p>;
  };

  const bodyParentUnitCost = (item) => {
    return <p>{convertToCurrencyFormat(item.unit_cost)}</p>;
  };

  const bodyParentTotalCost = (item) => {
    return <p>{convertToCurrencyFormat(item.total_cost)}</p>;
  };

  const bodyParentLocation = (item) => {
    return (
      <p>
        {item.warehouse_name} <br />
        {item.warehouse_location_name}
      </p>
    );
  };

  const onRowEditComplete = (e) => {
    const { data, newData } = e;

    if (newData.unit_cost !== data.unit_cost && newData.unit_cost >= 0) {
      onUpdateUnitCostForReceivingItem({
        unique_id: newData.unique_id,
        plu: newData.plu,
        lot_number: newData.lot_number,
        unit_cost: newData.unit_cost,
        warehouse_location_id: newData.warehouse_location_id,
        is_paginated: isPaginated,
        page: currentPage,
      });
    }

    if (newData.total_cost !== data.total_cost && newData.total_cost >= 0) {
      onUpdateTotalCostForReceivingItem({
        unique_id: newData.unique_id,
        plu: newData.plu,
        lot_number: newData.lot_number,
        total_cost: newData.total_cost,
        warehouse_location_id: newData.warehouse_location_id,
        total_weight: newData.total_weight,
        total_pieces: newData.total_pieces,
        stock_unit_of_measurement: newData.stock_unit_of_measurement,
      });
    }
  };

  const unitPriceEditor = (options) => {
    return (
      <InputNumber
        value={options.value}
        onValueChange={(e) => options.editorCallback(e.value)}
        mode="currency"
        currency="USD"
        locale="en-US"
        onKeyDown={(e) => e.stopPropagation()}
      />
    );
  };

  const cartonCountBody = (item) => {
    return <p>{item.carton_count} cartons</p>;
  };

  const isReadOnly = isReadOnlyUser();

  return (
    <div className="flow-root">
      <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 space-y-4">
          <div className="w-full text-right">
            {!isReadOnly && (
              <Button
                variant="danger"
                className="ml-2 px-1.5 py-1.5"
                onClick={askReturnSelectedItems}
              >
                Delete
              </Button>
            )}
          </div>
          {loading ? (
            <TableLoadingSkeleton rows={5} columns={4} />
          ) : (
            <div className="overflow-hidden card-custom bg-white">
              <DataTable
                value={items}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                }}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
                tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                cellClassName="p-2 border-1-bottom-gray text-sm"
                editMode="row"
                onRowEditComplete={onRowEditComplete}
              >
                <Column
                  header="Product Name"
                  body={bodyProductName}
                  style={{ width: "25%" }}
                />
                <Column
                  field="lot_number"
                  header="Lot Number"
                  style={{ width: "15%" }}
                />
                <Column
                  header="Carton Count"
                  body={cartonCountBody}
                  style={{ width: "10%" }}
                />
                <Column
                  header="Unit Wt"
                  body={bodyParentItemWeight}
                  style={{ width: "5%" }}
                />
                <Column
                  header="Total Wt"
                  body={bodyParentTotalWeight}
                  style={{ width: "5%" }}
                />
                <Column
                  header="Total Pieces"
                  body={bodyParentPieceCount}
                  style={{ width: "8%" }}
                />
                <Column
                  field="unit_cost"
                  header="Unit Cost"
                  body={bodyParentUnitCost}
                  style={{ width: "8%" }}
                  editor={(options) => unitPriceEditor(options)}
                />
                <Column
                  field="total_cost"
                  header="Total Cost"
                  body={bodyParentTotalCost}
                  style={{ width: "8%" }}
                  editor={(options) => unitPriceEditor(options)}
                />
                <Column
                  body={bodyParentLocation}
                  header="Location"
                  style={{ width: "15%" }}
                />
                {!isReadOnly && (
                  <Column
                    rowEditor={!closed}
                    headerStyle={{ minWidth: "6rem" }}
                    bodyStyle={{ textAlign: "center" }}
                  />
                )}
                <Column expander={allowExpansion} style={{ width: "6%" }} />
              </DataTable>
              {isPaginated ? (
                <div className="w-full">
                  <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPageChange={(event) => {
                      const nextPage = event.page + 1;
                      setFirst(event.first);
                      setRows(event.rows);
                      setCurrentPage(nextPage);
                      if (onFetchPage) onFetchPage(nextPage);
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
